import { ApiError } from '@api/client'
import type { StaffSession } from '@api/types'
import { clearStaffSession, readStaffSession, saveStaffSession } from '@lib/staff-session'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

type StaffSessionValue = {
	session: StaffSession | null
	signIn: (session: StaffSession) => void
	signOut: () => void
}

const StaffSessionContext = createContext<StaffSessionValue | null>(null)

// Sesión del personal en el panel: arranca con la guardada en el navegador (si sigue vigente).
// Al cerrarla se olvidan también los datos del panel en caché.
export const StaffSessionProvider = ({ children }: { children: ReactNode }) => {
	const queryClient = useQueryClient()
	const [session, setSession] = useState<StaffSession | null>(() => readStaffSession())

	const signIn = useCallback((next: StaffSession) => {
		saveStaffSession(next)
		setSession(next)
	}, [])

	const signOut = useCallback(() => {
		clearStaffSession()
		setSession(null)
		queryClient.removeQueries({ queryKey: ['panel'] })
	}, [queryClient])

	const value = useMemo(() => ({ session, signIn, signOut }), [session, signIn, signOut])
	return <StaffSessionContext.Provider value={value}>{children}</StaffSessionContext.Provider>
}

export const useStaffSession = (): StaffSessionValue => {
	const value = useContext(StaffSessionContext)
	if (!value) throw new Error('useStaffSession fuera de StaffSessionProvider')
	return value
}

export const isUnauthorized = (error: unknown): boolean => error instanceof ApiError && error.status === 401

// Reintentos de una consulta del panel: los de la app, salvo un 401 (sesión caducada o
// persona desactivada), que no se arregla reintentando.
export const retryUnlessUnauthorized =
	(base: unknown) =>
	(failureCount: number, error: unknown): boolean => {
		if (isUnauthorized(error)) return false
		if (typeof base === 'number') return failureCount < base
		if (typeof base === 'function') return Boolean(base(failureCount, error))
		if (typeof base === 'boolean') return base
		return failureCount < 3 // el valor por defecto de TanStack Query
	}

// Consulta autenticada del panel. Si la API responde 401, la sesión se cierra y vuelve el
// formulario de acceso.
export const usePanelQuery = <T,>(key: readonly unknown[], fetcher: (token: string) => Promise<T>) => {
	const { session, signOut } = useStaffSession()
	const queryClient = useQueryClient()
	const token = session?.token ?? ''
	const query = useQuery({
		queryKey: ['panel', ...key],
		queryFn: () => fetcher(token),
		enabled: token !== '',
		retry: retryUnlessUnauthorized(queryClient.getDefaultOptions().queries?.retry),
	})
	useEffect(() => {
		if (isUnauthorized(query.error)) signOut()
	}, [query.error, signOut])
	return query
}
